const toArray = (value) => (Array.isArray(value) ? value : Object.values(value || {}));

const dot = (a, b) => a.reduce((sum, value, index) => sum + value * (b[index] || 0), 0);

const norm = (a) => Math.sqrt(a.reduce((sum, value) => sum + value * value, 0));

export const cosineSimilarity = (a, b) => {
  const vectorA = toArray(a);
  const vectorB = toArray(b);
  const length = Math.max(vectorA.length, vectorB.length);
  const paddedA = Array.from({ length }, (_, index) => vectorA[index] || 0);
  const paddedB = Array.from({ length }, (_, index) => vectorB[index] || 0);
  const denominator = norm(paddedA) * norm(paddedB);
  if (denominator === 0) return 0;
  return dot(paddedA, paddedB) / denominator;
};

export const jaccardSimilarity = (setA, setB) => {
  const a = new Set(setA || []);
  const b = new Set(setB || []);
  if (a.size === 0 && b.size === 0) return 0;
  const intersection = [...a].filter((value) => b.has(value)).length;
  const union = new Set([...a, ...b]).size;
  return union === 0 ? 0 : intersection / union;
};

export const pearsonCorrelation = (x, y) => {
  const n = Math.min(x.length, y.length);
  if (n < 2) return 0;
  const meanX = x.slice(0, n).reduce((sum, value) => sum + value, 0) / n;
  const meanY = y.slice(0, n).reduce((sum, value) => sum + value, 0) / n;
  let numerator = 0;
  let denominatorX = 0;
  let denominatorY = 0;
  for (let index = 0; index < n; index += 1) {
    const dx = x[index] - meanX;
    const dy = y[index] - meanY;
    numerator += dx * dy;
    denominatorX += dx * dx;
    denominatorY += dy * dy;
  }
  const denominator = Math.sqrt(denominatorX * denominatorY);
  return denominator === 0 ? 0 : numerator / denominator;
};

export const euclideanDistance = (a, b) => {
  const vectorA = toArray(a);
  const vectorB = toArray(b);
  const length = Math.max(vectorA.length, vectorB.length);
  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    const difference = (vectorA[index] || 0) - (vectorB[index] || 0);
    sum += difference * difference;
  }
  return Math.sqrt(sum);
};
